import {
  type RowData,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui";

import { type DataTableFeatures } from "src/components/dataTableFeatures";

interface DataTablePaginationProps<TData extends RowData> {
  table: TanstackTable<DataTableFeatures, TData>;
  pageSizeOptions: number[];
  rowCount?: number;
  hideRowsPerPage?: boolean;
}

const iconClassName = "size-4";

export function DataTablePagination<TData extends RowData>(
  props: DataTablePaginationProps<TData>,
) {
  const { t } = useTranslation();
  const state = props.table.atoms.pagination.get();
  const pageCount = props.table.getPageCount();
  const total = props.rowCount ?? props.table.getFilteredRowModel().rows.length;
  const from = total === 0 ? 0 : state.pageIndex * state.pageSize + 1;
  const to = Math.min(total, (state.pageIndex + 1) * state.pageSize);

  const sortedOptions = [...props.pageSizeOptions].sort((a, b) => {
    return a - b;
  });
  const firstOver = sortedOptions.find((opt) => {
    return opt >= total;
  });
  const visibleOptions =
    total > 0
      ? sortedOptions.filter((opt) => {
          if (opt === state.pageSize) return true;

          return opt < total || opt === firstOver;
        })
      : sortedOptions;

  return (
    <div className="flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-muted-foreground">
        {t("dataTable.showing", { from, to, total })}
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-6">
        {!props.hideRowsPerPage && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t("dataTable.rowsPerPage")}
            </span>
            <Select
              value={`${state.pageSize}`}
              onValueChange={(value) => {
                props.table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger size="sm" className="w-[72px]">
                <SelectValue placeholder={state.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {visibleOptions.map((size) => {
                  return (
                    <SelectItem key={size} value={`${size}`}>
                      {size}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="text-sm text-muted-foreground">
          {t("dataTable.pageOf", {
            page: state.pageIndex + 1,
            total: Math.max(1, pageCount),
          })}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t("dataTable.firstPage")}
            onClick={() => {
              props.table.setPageIndex(0);
            }}
            disabled={!props.table.getCanPreviousPage()}
          >
            <Icon name="tabler-chevrons-left" className={iconClassName} />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t("dataTable.previousPage")}
            onClick={() => {
              props.table.previousPage();
            }}
            disabled={!props.table.getCanPreviousPage()}
          >
            <Icon name="tabler-chevron-left" className={iconClassName} />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t("dataTable.nextPage")}
            onClick={() => {
              props.table.nextPage();
            }}
            disabled={!props.table.getCanNextPage()}
          >
            <Icon name="tabler-chevron-right" className={iconClassName} />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t("dataTable.lastPage")}
            onClick={() => {
              props.table.setPageIndex(Math.max(0, pageCount - 1));
            }}
            disabled={!props.table.getCanNextPage()}
          >
            <Icon name="tabler-chevrons-right" className={iconClassName} />
          </Button>
        </div>
      </div>
    </div>
  );
}
